/**
 * Aplicar una propuesta aprobada — **con las mutaciones que ya existen**.
 *
 * Aquí no hay ni una sentencia SQL nueva. Cada pieza se traduce a la misma
 * mutación que usa el editor a mano (`addUnitMutation`, `addLessonModuleMutation`,
 * `addBlockMutation`, `updateBlockMutation`, …), que escribe por el write-path
 * normal de PowerSync. De ahí salen gratis tres cosas que, escritas aparte,
 * serían tres features más: el **feed de actividad**, la **papelera** y el
 * **deshacer**.
 *
 * Dos invariantes:
 *
 * 1. **Solo se aplica lo aprobado y válido.** El veredicto lo pone `validate.ts`
 *    cruzando los dos registros; esta función no valida nada por su cuenta ni se
 *    fía del `valid` que trajo el servidor.
 * 2. **El orden es por dependencia**, no el que devolvió el modelo: la unidad
 *    antes que su módulo, el módulo antes que su bloque. Si no, quedan hijos
 *    apuntando a padres que todavía no existen.
 */

import type { PowerSyncDrizzleDB } from "@space/powersync";
import { addBlockMutation, addUnitMutation, updateBlockMutation } from "../../courses/mutations";
import {
  addCourseModuleMutation,
  addLessonModuleMutation,
  addResourceModuleMutation,
  updateCourseModuleMutation,
} from "../modules/mutations";
import type { AiItemDecision, AiProposalItem } from "./contract";
import { type ItemVerdict, sortForApply } from "./validate";

export interface ApplyContext {
  db: PowerSyncDrizzleDB;
  courseId: string;
  /** Unidad destino cuando la propuesta no crea la suya (bloques sueltos). */
  defaultUnitId?: string;
  /** Lección destino para las piezas `block` de una propuesta de contenido. */
  defaultLessonId?: string;
}

export interface ApplyResult {
  /** Decisiones a mandar al servidor (`POST /proposals/:id/decide`). */
  decisions: AiItemDecision[];
  /** Ids creados, por `path` de la pieza. */
  createdByPath: Map<string, string>;
  failures: { itemId: string; message: string }[];
}

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function num(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Unidad a la que pertenece una pieza, resolviendo el padre ya creado. */
function parentUnitId(item: AiProposalItem, created: Map<string, string>, ctx: ApplyContext) {
  // `/units/2/modules/0` → su unidad es `/units/2`.
  const match = /^(\/units\/\d+)\//.exec(item.path);
  const parentPath = match?.[1];
  if (parentPath) {
    const parent = created.get(parentPath);
    if (parent) {
      return parent;
    }
  }
  const declared = text(item.payload.parentId) || text(item.payload.unitId);
  return declared || ctx.defaultUnitId || "";
}

/**
 * Aplica las piezas aprobadas y devuelve las decisiones que hay que registrar.
 *
 * Es tolerante a fallos por pieza a propósito: que una lección falle no puede
 * tirar las otras cuatro que sí valían, y lo que falla queda marcado `failed`
 * con su motivo en vez de desaparecer.
 */
export async function applyApprovedItems(
  items: readonly AiProposalItem[],
  verdicts: ReadonlyMap<string, ItemVerdict>,
  approvedIds: ReadonlySet<string>,
  ctx: ApplyContext,
): Promise<ApplyResult> {
  const decisions: AiItemDecision[] = [];
  const createdByPath = new Map<string, string>();
  const failures: { itemId: string; message: string }[] = [];

  for (const item of sortForApply(items)) {
    if (!approvedIds.has(item.id)) {
      continue;
    }
    const verdict = verdicts.get(item.id);
    if (!verdict?.valid) {
      // Defensa en profundidad: el servidor también lo rechaza (`AND valid`),
      // pero no se le manda una aplicación que sabemos inválida.
      decisions.push({
        itemId: item.id,
        status: "rejected",
        valid: false,
        errors: verdict?.errors ?? ["la pieza no pasó la validación"],
      });
      continue;
    }
    try {
      const entityId = await applyOne(item, createdByPath, ctx);
      if (entityId) {
        createdByPath.set(item.path, entityId);
      }
      decisions.push({ itemId: item.id, status: "applied", entityId, valid: true, errors: [] });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      failures.push({ itemId: item.id, message });
      decisions.push({ itemId: item.id, status: "failed", valid: true, errors: [message] });
    }
  }
  return { decisions, createdByPath, failures };
}

async function applyOne(
  item: AiProposalItem,
  created: Map<string, string>,
  ctx: ApplyContext,
): Promise<string | undefined> {
  const { db, courseId } = ctx;
  switch (item.entity) {
    case "unit": {
      if (item.op === "create") {
        return addUnitMutation(db, courseId, {
          title: text(item.payload.title, "Nueva unidad"),
          position: num(item.payload.position, 0),
        });
      }
      break;
    }
    case "course_module": {
      const unitId = parentUnitId(item, created, ctx);
      if (!unitId) {
        throw new Error("no se sabe en qué unidad va este módulo");
      }
      if (item.op === "create") {
        const kind = text(item.payload.kind);
        const title = text(item.payload.title, "Nuevo módulo");
        const position = num(item.payload.position, 0);
        // Los kinds con detalle propio usan su mutación COMPUESTA, que crea la
        // fila de detalle y su módulo de espina de una vez. Crear solo el
        // módulo dejaría un `ref_id` colgando.
        if (kind === "lesson") {
          const { moduleId } = await addLessonModuleMutation(db, {
            courseId,
            unitId,
            title,
            position,
          });
          return moduleId;
        }
        if (kind === "resource") {
          const { moduleId } = await addResourceModuleMutation(db, {
            courseId,
            unitId,
            title,
            position,
          });
          return moduleId;
        }
        return addCourseModuleMutation(db, {
          courseId,
          unitId,
          kind,
          position,
          config: item.payload.config ?? {},
        });
      }
      if (item.op === "update" && item.entityId) {
        await updateCourseModuleMutation(db, item.entityId, {
          config: item.payload.config ?? {},
          position: num(item.payload.position, 0),
        });
        return item.entityId;
      }
      break;
    }
    case "block": {
      const lessonId = text(item.payload.lessonId) || ctx.defaultLessonId || "";
      if (item.op === "create") {
        if (!lessonId) {
          throw new Error("no se sabe en qué lección va este bloque");
        }
        return addBlockMutation(db, {
          courseId,
          lessonId,
          kind: text(item.payload.type),
          content: item.payload.def ?? {},
          position: num(item.payload.position, 0),
          weight: num(item.payload.weight, 1),
        });
      }
      if (item.op === "update" && item.entityId) {
        await updateBlockMutation(db, item.entityId, { content: item.payload.def ?? {} });
        return item.entityId;
      }
      break;
    }
    default: {
      break;
    }
  }
  throw new Error(`no hay forma de aplicar «${item.op} ${item.entity}» automáticamente`);
}
